"use client";

import { useEffect, useState } from "react";
import {
  Trophy,
  Calendar,
  Award,
  Code2,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Upload,
  Sparkles,
  Flower2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { challengeAPI } from "../lib/studentAPI";

export default function MonthlyChallenges() {
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [code, setCode] = useState<Record<string, string>>({});
  const [videos, setVideos] = useState<Record<string, File | null>>({});
  const [submissions, setSubmissions] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});

  // Success/Failure Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"success" | "error">("success");
  const [modalMessage, setModalMessage] = useState("");

  // LOAD DATA
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const challengesData = await challengeAPI.getChallenges();
        setChallenges(challengesData || []);

        const submissionsData = await challengeAPI.getSubmissions();
        const mapped: Record<string, any> = {};
        (submissionsData || []).forEach((s: any) => {
          mapped[s.challengeId] = s;
        });
        setSubmissions(mapped);
      } catch (err: any) {
        console.error(err);
        toast.error("Failed to load challenges");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleVideoChange = (challengeId: string, file: File | null) => {
    setVideos(prev => ({ ...prev, [challengeId]: file }));
  };

  const handleSubmit = async (challengeId: string) => {
    const submissionCode = code[challengeId]?.trim();

    if (!submissionCode) {
      toast.error("Please write your solution before submitting!");
      return;
    }

    try {
      setSubmitting(prev => ({ ...prev, [challengeId]: true }));

      const data = await challengeAPI.submitChallenge(challengeId, submissionCode);

      setSubmissions(prev => ({ ...prev, [challengeId]: data }));

      // Show beautiful success modal
      setModalType("success");
      setModalMessage("Amazing work! Your solution has been submitted successfully! 🌸");
      setShowModal(true);

      setCode(prev => ({ ...prev, [challengeId]: "" }));
      setVideos(prev => ({ ...prev, [challengeId]: null }));
      setActiveId(null);

    } catch (err: any) {
      console.error(err);
      const errorMsg = err?.message || "Something went wrong. Please try again.";

      setModalType("error");
      setModalMessage(errorMsg);
      setShowModal(true);
    } finally {
      setSubmitting(prev => ({ ...prev, [challengeId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-purple-700 font-medium">Loading challenges...</p>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 pb-20 relative overflow-hidden">
    {/* Decorative Flowers Background */}
    <div className="absolute top-10 left-10 text-purple-200 text-6xl opacity-30 pointer-events-none">🌸</div>
    <div className="absolute top-40 right-20 text-pink-200 text-7xl opacity-30 pointer-events-none rotate-12">🌺</div>
    <div className="absolute bottom-32 left-1/3 text-purple-200 text-5xl opacity-30 pointer-events-none">🌼</div>
    <div className="absolute bottom-20 right-1/4 text-pink-200 text-6xl opacity-30 pointer-events-none -rotate-12">🌷</div>

    <div className="max-w-7xl mx-auto px-6 pt-10">
      {/* Back Button */}
      <Button
        variant="outline"
        className="mb-8 border-purple-300 text-purple-700 hover:bg-purple-100"
        onClick={() => navigate("/dashboard")}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex items-center gap-6 mb-12">
        <div className="p-5 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-3xl shadow-xl">
          <Trophy className="w-14 h-14" />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
              Monthly Innovation Challenge
            </h1>
            <Sparkles className="w-8 h-8 text-yellow-500" />
          </div>
          <p className="text-xl text-purple-700 mt-2 flex items-center gap-2">
            Solve • Create • Shine like a star! ✨
          </p>
        </div>
      </div>

      {challenges.length === 0 ? (
        <Card className="p-20 text-center border-purple-200">
          <Flower2 className="w-24 h-24 mx-auto text-purple-300 mb-6" />
          <h3 className="text-3xl font-semibold text-purple-800">No Challenges Yet</h3>
          <p className="text-purple-600 mt-3">Check back soon for new exciting challenges!</p>
        </Card>
      ) : (
        <div className="space-y-10">
          {challenges.map((c) => {
            const hasSubmitted = !!submissions[c.id];
            const isActive = activeId === c.id;
            const isSubmitting = submitting[c.id];

            return (
              <Card
                key={c.id}
                className="shadow-xl border-2 border-purple-100 hover:border-purple-300 transition-all duration-300 overflow-hidden"
              >
                <CardHeader className="bg-gradient-to-r from-purple-100 to-pink-100 pb-8">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-3xl font-bold text-purple-900">
                      {c.title}
                    </CardTitle>
                    
                    {hasSubmitted && (
                      <Badge className="bg-green-500 hover:bg-green-600 text-white px-4 py-1 text-sm flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4" />
                        Submitted Successfully!
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="p-8 space-y-8">
                  <div className="bg-white/70 backdrop-blur-sm p-8 rounded-2xl border border-purple-100 text-lg leading-relaxed text-gray-700">
                    {c.description}
                  </div>

                  <div className="flex gap-8 text-sm text-purple-700">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      <span>Deadline: {new Date(c.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    {c.rewards && (
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-500" />
                        <span>{c.rewards}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => setActiveId(isActive ? null : c.id)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg rounded-2xl shadow-lg"
                  >
                    <Code2 className="w-5 h-5 mr-3" />
                    {isActive ? "Close Editor" : "Open Solution Editor"}
                  </Button>

                  {/* EDITOR SECTION */}
                  {isActive && (
                    <div className="pt-8 border-t border-purple-200 space-y-6">
                      <div>
                        <label className="text-purple-700 font-semibold mb-3 block">Your Creative Solution ✨</label>
                        <Textarea
                          className="min-h-[420px] text-base font-mono bg-zinc-950 text-emerald-400 border-purple-300 focus:border-purple-500 rounded-2xl p-6"
                          placeholder="Write your amazing solution here..."
                          value={code[c.id] || ""}
                          onChange={(e) =>
                            setCode(prev => ({ ...prev, [c.id]: e.target.value }))
                          }
                        />
                      </div>

                      <div className="space-y-3">
                        <label className="text-purple-700 font-medium">Demo Video (Optional - Makes your submission extra special!)</label>
                        <div className="flex items-center gap-4 p-4 border-2 border-dashed border-purple-300 rounded-2xl hover:border-purple-400 transition-colors">
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => handleVideoChange(c.id, e.target.files?.[0] || null)}
                            className="text-sm"
                          />
                          <Upload className="w-6 h-6 text-purple-500" />
                        </div>
                        {videos[c.id] && (
                          <p className="text-green-600 font-medium">🌟 Selected: {videos[c.id]?.name}</p>
                        )}
                      </div>

                      <div className="flex gap-4 pt-4">
                        <Button
                          onClick={() => handleSubmit(c.id)}
                          disabled={isSubmitting}
                          className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg py-7 rounded-2xl shadow-xl"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin mr-3" />
                              Submitting Your Brilliant Work...
                            </>
                          ) : (
                            "🚀 Submit My Solution"
                          )}
                        </Button>

                        <Button
                          variant="outline"
                          onClick={() => setActiveId(null)}
                          className="px-10 border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>

    {/* SUCCESS / FAILURE MODAL */}
    {showModal && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        {/* Modal Box */}
        <div
          className={`
            bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full mx-4 text-center border-4
            ${modalType === "success" ? "border-green-400" : "border-red-400"}
            transform transition-all duration-300 scale-100
          `}
        >
          {/* SUCCESS */}
          {modalType === "success" ? (
            <>
              <div className="text-7xl mb-6">🎉🌸✨</div>
              <h2 className="text-3xl font-bold text-green-700 mb-3">
                Submission Successful!
              </h2>
              <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                {modalMessage}
              </p>
              <div className="text-4xl mb-6">🌺🌼🌷</div>
            </>
          ) : (
            /* ERROR */
            <>
              <div className="text-7xl mb-6">😕</div>
              <h2 className="text-3xl font-bold text-red-600 mb-3">
                Submission Failed
              </h2>
              <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                {modalMessage}
              </p>
            </>
          )}

          {/* ACTION BUTTON */}
          <Button
            onClick={() => setShowModal(false)}
            className={`
              w-full py-6 text-lg rounded-2xl text-white
              ${modalType === "success"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"}
            `}
          >
            {modalType === "success"
              ? "Yay! Back to Challenges"
              : "Try Again"}
          </Button>
        </div>
      </div>
    )}
  </div>
);
}